import Service, { inject as service } from "@ember/service"
import type { AiMessageDelta, UserMessageData } from "@shared/models/ai"
import type { Channel } from "cc-frontend/lib/phoenix"
import { default as ENV } from "../config/environment"
import type Session from "./session"
import type SocketService from "./socket"

interface AiChatInfo {
  readonly chatId: string
  readonly channel: Channel
  readonly onMessageDeltas: (deltas: AiMessageDelta[]) => void
  readonly onMessageCompleted: () => void
  readonly onRateLimited: (message: string) => void
  readonly onError: (error: unknown, isRetryable: boolean) => void
}

type JoinError =
  | {
      type: "error"
      message: string
    }
  | { type: "rate-limited"; message: string }

/**
 * The timeout to send a message or join the channel, in ms.
 * The client will queue the message and keep trying to reconnect (w/ exponential backoff) for this long.
 */
const SEND_TIMEOUT = 30_000

export default class AiChats extends Service {
  @service declare socket: SocketService
  @service declare session: Session

  /** Info for the currently-active chats, keyed by chatId. */
  private chats = new Map<string, AiChatInfo>()

  /**
   * Creates a new AI chat, establishing a Channel to talk to the server.
   *
   * @param chatId A new ObjectId for the chat.
   * @param onError Callback to show an error to the user.
   */
  create(
    chatId: string,
    cardStackId: string,
    lessonMeta: {
      courseId: string
      planbookId: string
      dateString: string
      isUnified?: boolean
    },
    onMessageDeltas: (deltas: AiMessageDelta[]) => void,
    onMessageCompleted: () => void,
    onRateLimited: (message: string) => void,
    onError: (error: unknown, isRetryable: boolean) => void
  ) {
    if (!this.session.token) {
      onError("Not logged in", false)
      return
    }
    if (!this.socket.socket) {
      this.socket.connect()
      if (!this.socket.socket) {
        // TODO: Make all join errors retryable?
        onError("Socket not connected", false)
        return
      }
    }

    console.log(`Joining channel ai-chats:${chatId}`)
    const channel = this.socket.socket.channel(`ai-chats:${chatId}`, {
      token: this.session.token,
      clientVersion: ENV.CLIENT_VERSION,
      cardStackId,
      lessonMeta,
    })
    channel.onError((error) => {
      // TODO: Recover from these errors automatically using reconnections.
      // For now, these need to be manually retried.
      // Show the Try Again message immediately instead of waiting for a timeout.
      console.log("AI channel generic error", error)
      onError(error, true)
    })
    channel.onClose((reason) => {
      console.log("AiChat channel closed gracefully", chatId, reason)
    })
    channel.on("message-deltas", ({ deltas }: { deltas: AiMessageDelta[] }) => {
      try {
        onMessageDeltas(deltas)
      } catch (e) {
        onError(e, true)
      }
    })
    channel.on("message-completed", ({ ok }: { ok: boolean }) => {
      if (ok) {
        try {
          onMessageCompleted()
        } catch (e) {
          onError(e, true)
        }
      } else {
        onError("LLMChain.run returned error", true)
      }
    })

    this.chats.set(chatId, {
      chatId,
      channel,
      onMessageDeltas,
      onMessageCompleted,
      onRateLimited,
      onError,
    })
    channel
      .join(SEND_TIMEOUT)
      .receive("ok", () => {
        console.log("AiChat channel joined successfully", chatId)
      })
      .receive("error", (error: JoinError) => {
        if (error.type === "rate-limited") {
          // The server records the LESSON_AI_RATE_LIMITED analytics event when it refuses the
          // join, so nothing needs to be dispatched here.
          onRateLimited(error.message)
          // Don't retry connecting - it won't change the result.
          channel.leave()
        } else {
          // Join handler on the server crashed.
          onError(error.message, false)
        }
      })
      .receive("timeout", () => onError("Join timeout", false))
  }

  /**
   * Sends the next user message in the given AI chat.
   */
  sendMessage(chatId: string, messageData: UserMessageData): void {
    const info = this.chats.get(chatId)
    if (!info) {
      throw new Error(`AI chat ${chatId} not found`)
    }

    const { channel, onError } = info
    channel
      .push("user-message", messageData, SEND_TIMEOUT)
      .receive("ok", () => {
        console.log("AiChat message sent successfully", chatId)
      })
      .receive("error", (error) => {
        // Message handler on the server crashed.
        // (Note: That handler constructs the LLMChain but doesn't actually run it.)
        console.log("AiChat message error", error)
        onError(error, true)
      })
      .receive("timeout", () => onError("Message send timeout", true))
  }

  /**
   * Call this when an AI chat component is unmounted to close the channel.
   */
  close(chatId: string): void {
    const info = this.chats.get(chatId)
    if (!info) return

    console.log(`Leaving channel ai-chats:${chatId}`)
    info.channel.leave()
    this.chats.delete(chatId)
  }
}
